"use client";

import { LocalStorageKeys } from "@/configs";
import { RouteConfig } from "@/configs/types/route";
import { ApiStatusNumber, AuthService } from "@/lib/services";
import type { IAccountFinancial, IStripeConnect } from "@/lib/services/types/auth";
import type { IPublisher } from "@/lib/services/types/publisher";
import { removeAuthToken } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import React from "react";

import LoginDialog from "./login-dialog";

export interface AuthData {
  stripeConnect?: IStripeConnect | null;
  email: string;
  financial?: IAccountFinancial;
  publishers: IPublisher[];
}

export type AuthPendingAction = () => unknown;

export interface AuthContextType {
  user: AuthData | undefined | null;
  authData: AuthData | undefined | null;
  isLogging: boolean;
  isLogged: boolean;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  checkAuth: () => boolean;
  requireAuth: (retryAction?: AuthPendingAction) => boolean;
  openLoginModal: (pendingAction?: AuthPendingAction) => void;
  closeLoginModal: () => void;
}

export const AuthContext = React.createContext<AuthContextType>({
  user: undefined,
  authData: undefined,
  isLogging: true,
  isLogged: false,
  logout: () => {},
  refreshAuth: async () => {},
  checkAuth: () => false,
  requireAuth: () => false,
  openLoginModal: () => {},
  closeLoginModal: () => {},
});

function decodeJwt(token: string): any {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    return null;
  }
}

export function isTokenValid(): boolean {
  const jwt = Cookies.get(LocalStorageKeys.accountToken);
  if (!jwt) return false;

  try {
    const decodedToken = decodeJwt(jwt);
    if (!decodedToken) {
      removeAuthToken();
      return false;
    }
    if (decodedToken.exp && decodedToken.exp * 1000 < Date.now()) {
      removeAuthToken();
      return false;
    }
  } catch (err) {
    removeAuthToken();
    return false;
  }

  return true;
}

async function fetchLoginInfo(): Promise<AuthData | null> {
  const { error, data, code } = await AuthService.getLoginInfo();

  if (error) {
    if (code === ApiStatusNumber.ErrorAuth) {
      removeAuthToken();
    }
    return null;
  }

  return {
    email: data?.account?.email || "",
    financial: data?.account?.financial,
    stripeConnect: data?.account?.stripeConnect || null,
    publishers: data?.account?.publishers || [],
  };
}

export function AuthProvider({
  children,
  defaultHasToken,
}: {
  children: React.ReactNode;
  defaultHasToken?: boolean;
}) {
  const [hasToken, setHasToken] = React.useState<boolean | undefined>(defaultHasToken);

  React.useEffect(() => {
    setHasToken(isTokenValid());
  }, []);

  const {
    data: loginData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["loginInfo"],
    queryFn: fetchLoginInfo,
    enabled: !!hasToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const handleRefresh = React.useCallback(async () => {
    const valid = isTokenValid();
    setHasToken(valid);
    if (valid) {
      await refetch();
    }
  }, [refetch]);

  const authData = React.useMemo(() => {
    if (hasToken === undefined || (isLoading && hasToken === true)) {
      return undefined;
    }
    if (!hasToken || !loginData) {
      return null;
    }
    return loginData;
  }, [hasToken, isLoading, loginData]);

  const logout = React.useCallback(() => {
    removeAuthToken();
    window.location.href = RouteConfig.Login;
  }, []);

  const isLogging = hasToken === undefined || (isLoading && hasToken === true);
  const isLogged = !!hasToken;

  const [isLoginDialogOpen, setIsLoginDialogOpen] = React.useState(false);
  const pendingActionRef = React.useRef<AuthPendingAction | null>(null);

  /** Always re-reads the cookie - never stale. */
  const checkAuth = React.useCallback(() => {
    return isTokenValid();
  }, []);

  const openLoginModal = React.useCallback((action?: AuthPendingAction) => {
    pendingActionRef.current = action ?? null;
    setIsLoginDialogOpen(true);
  }, []);

  const closeLoginModal = React.useCallback(() => {
    pendingActionRef.current = null;
    setIsLoginDialogOpen(false);
  }, []);

  const requireAuth = React.useCallback(
    (retryAction?: AuthPendingAction): boolean => {
      if (checkAuth()) {
        return true;
      }
      openLoginModal(retryAction);
      return false;
    },
    [checkAuth, openLoginModal],
  );

  const handleLoginSuccess = React.useCallback(async () => {
    setIsLoginDialogOpen(false);

    await handleRefresh();

    const pending = pendingActionRef.current;
    pendingActionRef.current = null;

    if (pending) {
      try {
        await pending();
      } catch (err) {
        console.error("Failed to execute pending auth action:", err);
      }
    }
  }, [handleRefresh]);

  return (
    <AuthContext.Provider
      value={{
        user: authData,
        authData,
        isLogging,
        isLogged,
        logout,
        refreshAuth: handleRefresh,
        checkAuth,
        requireAuth,
        openLoginModal,
        closeLoginModal,
      }}
    >
      {children}
      <LoginDialog isOpen={isLoginDialogOpen} onClose={closeLoginModal} onSuccess={handleLoginSuccess} />
    </AuthContext.Provider>
  );
}
